import api from './api'
import supportFaq from '../config/supportFaq'
import { findMatchingSupportFaq } from './supportFaqMatcher'
import { STORAGE_KEYS } from '../utils/constants'

const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `sc-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const buildFallbackResponse = (content) => {
  const matchedFaq = findMatchingSupportFaq(content, supportFaq)

  const responseText = matchedFaq
    ? `Here is what I can share:\n\n**${matchedFaq.question}**\n${matchedFaq.answer}`
    : 'Thanks for the message. A support specialist will follow up shortly. Meanwhile, you can review the FAQ or email support@quantumhusky.ai.'

  const actions = []
  if (matchedFaq?.links?.length) {
    matchedFaq.links.forEach((link) => {
      actions.push({
        type: 'OPEN_LINK',
        label: link.label,
        url: link.url,
      })
    })
  }

  return {
    sessionId: null,
    messages: [
      {
        id: generateId(),
        role: 'assistant',
        content: responseText,
        createdAt: new Date().toISOString(),
        metadata: {
          fallback: true,
          matchedFaqId: matchedFaq?.id,
          actions,
        },
      },
    ],
  }
}

export { buildFallbackResponse }

export const createSupportChatSession = async (options = {}) => {
  try {
    const payload = {
      entryPoint: options.entryPoint || 'support_chat_launcher',
      locale: typeof navigator !== 'undefined' ? navigator.language : 'en-US',
    }
    const response = await api.post('/api/v1/support/chat/sessions', payload)
    return response.data || response
  } catch (error) {
    console.warn('Support chat session creation failed, using local session.', error)
    return {
      sessionId: generateId(),
      createdAt: new Date().toISOString(),
      fallback: true,
    }
  }
}

export const sendSupportChatMessage = async ({ sessionId, content }) => {
  try {
    const response = await api.post('/api/v1/support/chat/messages', {
      sessionId,
      message: content,
    })
    return response.data || response
  } catch (error) {
    console.warn('Support chat message failed, using fallback response.', error)
    const fallback = buildFallbackResponse(content)
    return {
      ...fallback,
      sessionId,
    }
  }
}

export const getSupportChatHistory = async (limit = 50) => {
  try {
    const response = await api.get('/api/v1/support/chat/history', {
      params: { limit },
    })
    return response.data || response
  } catch (error) {
    console.warn('Failed to load support chat history.', error)
    return {
      messages: [],
      hasMore: false,
      totalMessages: 0,
    }
  }
}

export const sendSupportChatMessageStream = ({ sessionId, content, onChunk, onDone, onError }) => {
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
  const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
  const url = `${baseUrl}/api/v1/support/chat/messages/stream`

  const signalDone = (() => {
    let notified = false
    return () => {
      if (!notified && typeof onDone === 'function') {
        notified = true
        onDone()
      }
    }
  })()

  fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({
      sessionId,
      message: content,
    }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      if (!response.body) {
        throw new Error('Readable stream is not supported in this browser.')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      const processEventBlock = (block) => {
        if (!block || !block.trim()) {
          return
        }

        const lines = block.split('\n')
        let eventType = 'message'
        const dataParts = []

        lines.forEach((rawLine) => {
          if (!rawLine) {
            return
          }

          const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine

          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim() || 'message'
            return
          }

          if (line.startsWith('data:')) {
            let value = line.slice(5)
            if (value.startsWith(' ')) {
              value = value.slice(1)
            }
            dataParts.push(value)
          }
        })

        if (eventType === 'done') {
          signalDone()
          return
        }

        if (dataParts.length === 0) {
          return
        }

        const payload = dataParts.join('\n')

        if (eventType === 'message') {
          if (typeof onChunk === 'function') {
            onChunk(payload)
          }
        }
      }

      const readStream = () => {
        return reader.read().then(({ done, value }) => {
          if (done) {
            if (buffer.trim().length > 0) {
              processEventBlock(buffer)
              buffer = ''
            }
            signalDone()
            return
          }

          buffer += decoder.decode(value, { stream: true })
          buffer = buffer.replace(/\r\n/g, '\n')

          let separatorIndex
          while ((separatorIndex = buffer.indexOf('\n\n')) >= 0) {
            const eventBlock = buffer.slice(0, separatorIndex)
            buffer = buffer.slice(separatorIndex + 2)
            processEventBlock(eventBlock)
          }

          return readStream()
        })
      }

      return readStream()
    })
    .catch((error) => {
      console.error('Stream error:', error)
      if (typeof onError === 'function') {
        onError(error)
      }
    })
}

export default {
  createSupportChatSession,
  sendSupportChatMessage,
  getSupportChatHistory,
  sendSupportChatMessageStream,
}
